import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, BookOpenIcon, CircleDollarSignIcon, ScaleIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { SaveButton } from '../SaveButton';
import { UniversityMark } from '../UniversityMark';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { campusImages } from '../../../data/discoverVisual';
import { deadlineState, nextDeadline } from '../../../utils/discover';
import { programmes as allProgrammes } from '../../../data/admissions';
import { cn } from '../../../utils/cn';
import type { UniversityProfile } from '../../../types/admissions';

/**
 * The Design B institution card: denser, built to be scanned three across.
 *
 * It answers the eight questions that decide whether a student opens an
 * institution — who, where, verified, international, what is taught, what
 * funding exists, when it closes, what to do next — and defers everything else
 * to the institution page rather than crowding the card.
 */
interface UniversityGridCardProps {
  university: UniversityProfile;
}
export function UniversityGridCard({
  university
}: UniversityGridCardProps) {
  const {
    isSaved,
    toggleSave,
    isCompared,
    toggleCompare,
    compareFull,
    matchFor,
    sort
  } = useDiscover();
  const owned = allProgrammes.filter(programme => programme.universityId === university.id);
  const soonest = nextDeadline(owned);
  const state = soonest ? deadlineState(soonest.deadline) : null;
  const compared = isCompared(university.id);
  const match = matchFor(university);
  return <article className="group flex h-full flex-col overflow-hidden rounded-card border border-line bg-surface transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-brand-200 hover:shadow-[0_14px_38px_rgba(15,23,42,0.1)]" data-id="element-1343" data-mp="1olslchlzbzfk">
      <div className="relative" data-id="element-1344" data-mp="3mdn6wfbry4aw">
        <img src={campusImages[university.id]} alt={`The ${university.name} campus`} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-1345" data-mp="3meg68v8elrj0" />
        <div className="absolute right-3 top-3" data-id="element-1346" data-mp="1za5j7s4v95x3">
          <SaveButton saved={isSaved('university', university.id)} onToggle={() => toggleSave('university', university.id)} subject={university.name} shape="heart" overlay data-id="element-1347" data-mp="1zchytyn5j46z" />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4" data-id="element-1348" data-mp="2d8vgp8vll7lz">
        <div className="flex items-start gap-3" data-id="element-1349" data-mp="2i0wnif9zy64m">
          <UniversityMark university={university} size="sm" className="-mt-8" data-id="element-1350" data-mp="2i27qicvmb9uo" />
          <div className="min-w-0 flex-1" data-id="element-1351" data-mp="2n09bj3cltj8s">
            <h3 className="flex items-center gap-1.5 text-body font-semibold text-ink" data-id="element-1352" data-mp="2n22iykv1k7hu">
              <Link to={`/discover/universities/${university.id}`} className="truncate rounded outline-none hover:underline focus-visible:underline" data-id="element-1353" data-mp="2s0xbwfmq3cfo">
                {university.name}
              </Link>
              {university.provenance === 'verified' && <BadgeCheckIcon aria-label="Published by the institution" className="h-4 w-4 shrink-0 text-brand-700" data-id="element-1354" data-mp="gdtt93u9ghnv" />}
            </h3>
            <p className="mt-0.5 truncate text-body-sm text-ink-muted" data-id="element-1355" data-mp="6fnvmyfhlxot">
              {university.city}, {university.country} · {university.type}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1356" data-mp="z30cqt2vpdaq">
          {university.provenance === 'verified' ? <Badge tone="ok" data-id="element-1357" data-mp="pcf92r8cliky">Verified</Badge> : <Badge tone="warn" data-id="element-1358" data-mp="pbw0400oe4j2">Not confirmed</Badge>}
          {university.internationalIntake && <Badge plain data-id="element-1359" data-mp="1mtdyy2ys8hxe">International intake</Badge>}
          {sort === 'Recommended' && match.score >= 85 && <Badge tone="brand" plain data-id="element-1360" data-mp="1hzi5ndtgrj8k">Top match</Badge>}
        </div>

        <dl className="cz-num mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-body-sm text-ink-muted" data-id="element-1361" data-mp="12itki3nqv59w">
          <div className="flex items-center gap-1.5" data-id="element-1362" data-mp="16lpux734rv2r">
            <BookOpenIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1363" data-mp="skvmwpjzvw4m" />
            <dt className="sr-only" data-id="element-1364" data-mp="smzx1p8hlrdc">Programmes</dt>
            <dd data-id="element-1365" data-mp="xl1iggtdy2rz">{owned.length} programmes</dd>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1366" data-mp="1l9kpyu4m2bj0">
            <CircleDollarSignIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1367" data-mp="1lavx93x9m1ev" />
            <dt className="sr-only" data-id="element-1368" data-mp="1q7ci9p963znq">Scholarships</dt>
            <dd data-id="element-1369" data-mp="1bjreru8dryfa">{university.scholarshipsAvailable} scholarships</dd>
          </div>
        </dl>

        <div className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-[minmax(0,1fr)_9rem]" data-id="element-1370" data-mp="1giumjmfqwiqy">
          <ul className="min-w-0 space-y-1" data-id="element-1371" data-mp="248f7gavglq2v">
            {owned.slice(0, 2).map(programme => <li key={programme.id} className="truncate text-body-sm text-ink" data-id="element-1372" data-mp="28z5dzrfmfyz5">
                {programme.title}
              </li>)}
            {owned.length === 0 && <li className="text-body-sm text-ink-muted" data-id="element-1373" data-mp="3tn7qoq1caqak">No programmes published yet</li>}
            {owned.length > 2 && <li className="text-caption text-brand-700" data-id="element-1374" data-mp="6cq8770tgyin">+{owned.length - 2} more programmes</li>}
          </ul>
          <div className="sm:border-l sm:border-line sm:pl-3" data-id="element-1375" data-mp="2berjqzx7nml">
            <p className="text-caption text-ink-subtle" data-id="element-1376" data-mp="3aq6m36bp6s75">
              {state?.closed === false ? 'Applications close' : 'Deadline'}
            </p>
            <p className={cn('cz-num mt-0.5 text-body-sm font-semibold', state && state.tone === 'danger' ? 'text-danger-700' : state && state.tone === 'warn' ? 'text-warn-800' : 'text-ink')} data-id="element-1377" data-mp="35tg76mbmq2nb">
              {soonest ? soonest.deadlineLabel.replace(/^Closes\s/, '') : 'Not published'}
            </p>
          </div>
        </div>

        <div className="mt-auto flex items-center gap-2 pt-4" data-id="element-1378" data-mp="3ntiv9q709uwy">
          <SaveButton saved={isSaved('university', university.id)} onToggle={() => toggleSave('university', university.id)} subject={university.name} data-id="element-1379" data-mp="3sslzx9j2pz4e" />
          <button type="button" onClick={() => toggleCompare(university.id)} disabled={!compared && compareFull} aria-pressed={compared} aria-label={compared ? `Remove ${university.name} from the comparison` : `Compare ${university.name}`} className={cn('flex min-h-touch min-w-touch items-center justify-center rounded-lg border transition-colors duration-150 ease-out disabled:cursor-not-allowed disabled:text-ink-subtle', compared ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink')} data-id="element-1380" data-mp="5oxpxc2wuf97">
            <ScaleIcon aria-hidden className="h-5 w-5" data-id="element-1381" data-mp="2itt0u68smjez" />
          </button>
          <Link to={`/discover/universities/${university.id}`} className="ml-auto min-w-0" data-id="element-1382" data-mp="2dvj7802pvqvh">
            <Button size="sm" variant="primary" icon={<ArrowRightIcon className="h-4 w-4" data-id="element-1384" data-mp="24vctqe70qqwb" />} data-id="element-1383" data-mp="28wew33w8fj2i">
              Explore programmes
            </Button>
          </Link>
        </div>
      </div>
    </article>;
}